import classNames from 'classnames'
import { useMemo, useRef } from 'react'
import { useTranslation } from 'next-i18next'

import todAnalytics from '~/analytics/tod/todAnalytics'
import { TodEvents } from '~/analytics/tod/todEvents.types'

import BookingFormLabel from '~/booking/components/BookingFormLabel/BookingFormLabel'
import SelectLanguage from '~/booking/components/SelectLanguage/SelectLanguage'
import SelectLevelsOfEducation from '~/booking/components/SelectLevelsOfEducation/SelectLevelsOfEducation'
import SelectLocation from '~/booking/components/SelectLocation/SelectLocation'
import SelectPhysicalLocation from '~/booking/components/SelectPhysicalLocation/SelectPhysicalLocation'
import SelectSubjects from '~/booking/components/SelectSubjects/SelectSubjects'
import SelectTutoringProduct from '~/booking/components/SelectTutoringProduct/SelectTutoringProduct'
import isLocationPhysical from '~/booking/utils/isLocationPhysical'
import { BookingLanguage, LevelOfEducation, Location, Subject } from '~/types/booking'
import { Product } from '~/types/product'
import { BookForSelfFormErrors, TutorForSelfBookingFields } from '~/types/cart'
import ToogleContent from '~/shared/components/ToogleContent/ToogleContent'
import { FieldError } from '~/components'

const noop = (): void => {}

interface Props {
  updatePrefPage?: boolean
  showYearsInSchool?: boolean
  showSubjects?: boolean
  showLanguage?: boolean
  showProducts?: boolean
  showLocation?: boolean
  allowOtherLocation?: boolean
  onLanguageChange: (language: BookingLanguage | null) => void
  onLevelsOfEducationClick: (levelsOfEducation: LevelOfEducation[]) => void
  onLocationChange?: (location: Location | null) => void
  onPhysicalLocationTypeClick?: (includeTravelExpense: boolean) => void
  onProductClick?: (product: Product) => void
  onSubjectClick: (subjects: Subject[]) => void
  form: TutorForSelfBookingFields
  errors?: BookForSelfFormErrors
}

export function BookingForSelfFormFields(props: Props): React.ReactElement {
  const {
    showYearsInSchool = true,
    showSubjects = true,
    showLanguage = true,
    showProducts = true,
    allowOtherLocation = true,
    showLocation = true,
    form,
    onLanguageChange,
    onLevelsOfEducationClick,
    onLocationChange = noop,
    onPhysicalLocationTypeClick = noop,
    onProductClick = noop,
    onSubjectClick,
    updatePrefPage = false,
    errors,
  } = props

  const { t } = useTranslation('booking')
  const { language, location, includeTravelExpense, product, levelsOfEducation, subjects } = form

  const isPhysicalLocation: boolean = useMemo(() => {
    return isLocationPhysical(location)
  }, [location])

  const locationSectionRef = useRef<HTMLDivElement>(null)
  const locationSectionContentRef = useRef<HTMLDivElement>(null)

  const locationCss = classNames({
    'h-44 sm:h-28': updatePrefPage,
  })

  const locationError = errors?.location
  const productError = errors?.product
  const levelsOfEducationError = errors?.levelsOfEducation
  const languageError = errors?.language
  const subjectsError = errors?.subjects

  const onLanguageChangeAnalyticsHOF = (value: BookingLanguage | null) => {
    todAnalytics.changeInSelectOption(TodEvents.LANGUAGE, { value })
    onLanguageChange(value)
  }

  const onLocationChangeAnalyticsHOF = (value: Location | null) => {
    todAnalytics.changeInSelectOption(TodEvents.LOCATION, { value })
    onLocationChange(value)
  }

  const onPhysicalLocationTypeClickAnalyticsHOF = (value: boolean) => {
    todAnalytics.changeInSelectOption(TodEvents.PHYSICAL_LOCATION, {
      value: value ? 'student' : 'teacher',
    })
    onPhysicalLocationTypeClick(value)
  }

  const onProductClickAnalyticsHOF = (value: Product) => {
    todAnalytics.changeInSelectOption(TodEvents.PRODUCT_SELECT, { value: value.hours })
    onProductClick(value)
  }

  const onLevelsOfEducationClickAnalyticsHOF = (value: LevelOfEducation[]) => {
    todAnalytics.changeInSelectOption(TodEvents.LEVELS_OF_EDUCATION, {
      value: value.join(','),
    })
    onLevelsOfEducationClick(value)
  }

  const onSubjectClickAnalyticsHOF = (value: Subject[]) => {
    todAnalytics.changeInSelectOption(TodEvents.SUBJECT_SELECT, { value: value.join(',') })
    onSubjectClick(value)
  }

  return (
    <>
      {showLocation && (
        <div>
          <section className={`p-4 bg-pink-light rounded ${locationError ? 'border-2 border-red' : ''}`}>
            <SelectLocation location={location} onChange={onLocationChangeAnalyticsHOF} />
          </section>
          <ToogleContent
            contentClasses={locationCss}
            contentRef={locationSectionContentRef}
            toogle={isPhysicalLocation}
            toogleRef={locationSectionRef}
            wrapperClasses={isPhysicalLocation ? 'mt-3' : ''}
          >
            <SelectPhysicalLocation
              allowOtherLocation={allowOtherLocation}
              includeTravelExpense={includeTravelExpense}
              onClick={onPhysicalLocationTypeClickAnalyticsHOF}
            />
          </ToogleContent>
          {locationError && <FieldError error={locationError} />}
        </div>
      )}

      {showProducts && (
        <div>
          <div className={productError ? 'rounded border-2 border-red' : ''}>
            <SelectTutoringProduct
              cart="tutorForSelf"
              includeTravelExpense={includeTravelExpense}
              onClick={onProductClickAnalyticsHOF}
              value={product}
            />
          </div>
          {productError && <FieldError error={productError} />}
        </div>
      )}

      {showYearsInSchool && (
        <div>
          <section
            className={`bg-pink-light p-4 rounded space-y-3 ${levelsOfEducationError ? 'border-2 border-red' : ''}`}
          >
            <BookingFormLabel>{t('selectGradeLevelForSelf')}</BookingFormLabel>
            <SelectLevelsOfEducation
              levelsOfEducation={levelsOfEducation}
              onClick={onLevelsOfEducationClickAnalyticsHOF}
            />
          </section>
          {levelsOfEducationError && <FieldError error={levelsOfEducationError} />}
        </div>
      )}

      {(showLanguage || showSubjects) && (
        <div>
          <section className="bg-pink-light p-4 rounded space-y-3">
            {showLanguage && (
              <>
                <BookingFormLabel>{t('selectTutoringLanguage')}</BookingFormLabel>
                <div>
                  <SelectLanguage error={languageError} language={language} onChange={onLanguageChangeAnalyticsHOF} />
                  {languageError && <FieldError error={languageError} />}
                </div>
              </>
            )}
            {showSubjects && (
              <>
                <BookingFormLabel>{t('selectSubjects')}</BookingFormLabel>
                <div>
                  <SelectSubjects error={subjectsError} onClick={onSubjectClickAnalyticsHOF} subjects={subjects} />
                  {subjectsError && <FieldError error={subjectsError} />}
                </div>
              </>
            )}
          </section>
        </div>
      )}
      <div>
        {locationError && <FieldError error={locationError} />}
        {productError && <FieldError error={productError} />}
        {levelsOfEducationError && <FieldError error={levelsOfEducationError} />}
        {languageError && <FieldError error={languageError} />}
        {subjectsError && <FieldError error={subjectsError} />}
      </div>
    </>
  )
}
